/**
 * Route copy for `src/routes/publishing` (keys: `routes.publishing.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  bulkBulkSchedulePage: {
    failedSuffix: ' · {{count}} failed',
    rowErrorsSuffix: ' · {{count}} row error(s)',
    theAccountAccountNameLabelIs: 'The account “{{account_name}}” ({{label}}) is not in the row\'s platforms — it will be ignored.',
    couldNotReadTheFile: 'Could not read the file.',
    totalDraftsDraftSProcessedTotal: '{{total_drafts}} draft(s) processed · {{total_jobs}} job(s) scheduled{{v}}.',
    parsedTotalRowsRowSParsed: '{{parsed}}/{{total_rows}} row(s) parsed · {{jobs}} job(s) scheduled{{v}}.',
    platforms: 'Platforms',
    default: 'default',
    targets: 'Targets',
    allActiveAccounts: 'All active accounts',
    text: 'Text',
    media: 'media',
    n1stComment: '1st comment',
    mediaOnly: '(media only)',
    customized: 'Customized:',
    scheduling: 'Scheduling',
    immediate: 'Immediate',
    media2: 'Media',
    status: 'Status',
    ready: '✓ Ready',
    error: '⚠ Error',
    result: 'Result',
    failed: '✕ Failed',
    scheduledTotalJobsJobS: '✓ Scheduled · {{total_jobs}} job(s)',
    noJob: 'No job',
    noActiveAccountForThesePlatforms: 'No active account for these platforms.',
    scheduledOn: 'Scheduled on',
    bulkSchedulingCsv: 'Bulk scheduling (CSV)',
    composeAndScheduleSeveralPostsAt: 'Compose and schedule several posts at once: paste rows or import a CSV, preview, then schedule.',
    loadAnExample: 'Load an example',
    reset: 'Reset',
    n1DataCsv: '1 · Data (CSV)',
    importACsv: 'Import a .csv',
    platformsTextTextTwitterFirstComment: 'platforms,text,text_twitter,first_comment,scheduled_at,media_urls,account_ids\nlinkedin|twitter,"My message, with a comma","Short version for X",Link in the comments,2026-09-01 09:30,,\ninstagram,Behind the scenes 📷,,,2026-09-01 12:00,https://cdn.example.com/a.jpg,',
    recognizedColumnsHeaderRecommendedCaseIn: 'Recognized columns (header recommended, case-insensitive):',
    platforms2: 'platforms',
    accountIds: 'account_ids',
    text2: 'text',
    title: 'title',
    scheduledAt: 'scheduled_at',
    mediaUrls: 'media_urls',
    firstComment: 'first_comment',
    customizeOneNetworkOfTheRow: '. Customize one network of the row with a column',
    textNetwork: 'text_<network>',
    titleNetwork: 'title_<network>',
    mediaUrlsNetwork: 'media_urls_<network>',
    or: 'or',
    firstCommentNetwork: 'first_comment_<network>',
    eG: '(e.g.',
    textTwitter: 'text_twitter',
    mediaUrlsInstagram: 'media_urls_instagram',
    anEmptyCellInheritsTheShared: ') — an empty cell inherits the shared value. Separate several values in one cell with',
    quotedFieldsMayContainCommasAnd: '. Quoted fields may contain commas and line breaks.',
    detected: 'Detected:',
    header: 'header',
    noHeaderPositional: 'no header (positional)',
    n2DefaultValues: '2 · Default values',
    forRowsThatOmitThem: '(for rows that omit them)',
    defaultPlatforms: 'Default platforms',
    defaultScheduling: 'Default scheduling',
    usedForRowsWithoutADate: 'Used for rows without a date. Empty = immediate publishing.',
    offsetBetweenRowsMinutes: 'Offset between rows (minutes)',
    spreadsOutRowsWithoutADate: 'Spreads out rows without a date: each row +N min after the previous one.',
    defaultTargetAccounts: 'Default target accounts',
    forRowsWithoutAnAccountIds: '(for rows without an account_ids column)',
    loadingAccounts: 'Loading accounts…',
    noConnectedAccount: 'No connected account',
    connectAtLeastOneNetworkFrom: 'Connect at least one network from the Platforms page to target the posts.',
    accountIdentifiersForTheAccountIds: 'Account identifiers (for the account_ids column)',
    n3Preview: '3 · Preview',
    tooManyValidDraftsLengthThe: 'Too many valid drafts ({{length}}). The maximum per batch is {{MAX_DRAFTS}} — trim the file or schedule in several batches.',
    noRowsToPreview: 'No rows to preview',
    pasteCsvRowsOrImportA: 'Paste CSV rows or import a file above, and the preview will appear here.',
    noRows: 'No rows',
    ready2: 'ready',
    errorcountWithErrorsIgnored: ' · {{errorCount}} with errors (ignored)',
    parseTheRawCsvOnThe: 'Parse the raw CSV on the server (backend parser) and schedule',
    serverParsing: 'Server parsing…',
    serverParserRawCsv: 'Server parser (raw CSV)',
    scheduling2: 'Scheduling…',
    scheduleLengthPostS: 'Schedule {{length}} post(s)',
    resultServerParser: 'Result (server parser)',
    rows: 'Rows',
    parsed: 'Parsed',
    scheduledJobs: 'Scheduled jobs',
    lengthRowErrorS: '{{length}} row error(s):',
    rowLineError: 'Row {{line}}: {{error}}',
    n4Result: '4 · Result',
    sendingTheBatch: 'Sending the batch…',
    drafts: 'Drafts',
    failures: 'Failures',
    noResults: 'No results',
    ready3: 'Ready',
  },
  bulkParse: {
    networks: 'networks',
    media: 'media',
    linkedinTwitterNewPost3Ideas: 'linkedin|twitter,"New post: 3 ideas, tested and approved.","3 tested and approved ideas 🧵 (short version for X)",The full link is in the article →,2026-09-01 09:30,,',
    instagramBehindTheScenesAtThe: 'instagram,"Behind the scenes at the workshop 📷",,,2026-09-01 12:00,https://cdn.example.com/atelier.jpg,',
    facebookNoDateWillUseThe: 'facebook,No date — will use the default scheduling.,,,,,',
    noPlatformEmptyCellAndNo: 'No platform (empty cell and no default platform chosen).',
    textOrMediaRequiredBothAre: 'Text or media required (both are empty).',
    invalidMediaUrlV: 'Invalid media URL: {{v}}.',
    platformCustomizationIgnoredThisNetworkI: '“{{platform}}” customization ignored: this network is not in the row\'s platforms.',
    invalidMediaUrlPlatformV: 'Invalid media URL ({{platform}}): {{v}}.',
    invalidSchedulingDateSchedulecell: 'Invalid scheduling date: “{{scheduleCell}}”.',
    scheduledDateInThePastLikely: 'Scheduled date in the past — likely immediate publishing.',
  },
  composerApi: {
    draftMarker: '(draft)',
    tenantNotFoundInTheSession: 'Tenant not found in the session — sign in again.',
  },
  composerExtras: {
    nameTheGroupAndEnterAt: 'Name the group and enter at least one hashtag.',
    hashtagGroups: '# Hashtag groups',
    loading: 'Loading…',
    noSavedGroupCreateOneBelow: 'No saved group. Create one below.',
    insert: 'Insert',
    deleteName: 'Delete {{name}}',
    suggestionsYourTopPerformingTags: 'Suggestions (your top-performing tags)',
    postsPostSLikesLikes: '{{posts}} post(s) · {{likes}} likes',
    newGroup: 'New group',
    nameEGSaasFr: 'Name (e.g. SaaS FR)',
    saasMarketingGrowthSpaceOrComma: '#saas #marketing #growth (space- or comma-separated)',
    saving: 'Saving…',
    saveTheGroup: 'Save the group',
    selectAtLeastOnePlatform: 'Select at least one platform.',
    perNetworkPreview: 'Per-network preview',
    generating: 'Generating…',
    preview: 'Preview',
    chars: 'chars',
    hashtagS: 'hashtag(s)',
    media: 'media',
    custom: 'custom',
    productTagsRemoved: 'Product tags removed.',
    lengthProductSTaggedOnLength2: '{{length}} product(s) tagged on {{length2}} network(s).',
    taggedProductsInstagramFacebook: '🛒 Tagged products (Instagram / Facebook)',
    appliedTo: 'Applied to:',
    firstSaveTheDraftSaveDraft: 'First save the draft (“Save draft” button) to be able to tag products in it.',
    loadingTags: 'Loading tags…',
    saveTheTags: 'Save the tags',
    instagramUpToIgMaxTags: 'Instagram: up to {{IG_MAX_TAGS_PER_MEDIA}} products per post · Facebook: up to',
    publishingRemainsSubjectToTheNetwork: '. Publishing remains subject to the network\'s Shopping approval.',
    perChannelQueues: '⏸ Per-channel queues',
    aPausedQueueHoldsThisChannel: 'A paused queue holds this channel\'s scheduled posts until it is resumed.',
    paused: 'Paused',
    resume: 'Resume',
    pause: 'Pause',
  },
  format: {
    xTwitter: 'X / Twitter',
    expired: 'Expired',
    revoked: 'Revoked',
    reconnectionRequired: 'Reconnection required',
    pending: 'Pending',
    inProgress: 'In progress',
    published: 'Published',
    failed: 'Failed',
    cancelled: 'Cancelled',
    awaitingApproval: 'Awaiting approval',
    noExpiry: 'No expiry',
    expiredRelAgo: 'Expired {{rel}} ago',
    expiresInRel: 'Expires in {{rel}}',
    active: 'Active',
  },
  instagramGridPlanner: {
    published: 'Published',
    scheduled: 'Scheduled',
    draft: 'Draft',
    unscheduled: 'Unscheduled',
    couldNotLoadTheInstagramGrid: 'Could not load the Instagram grid.',
    couldNotPreview: 'Could not preview.',
    couldNotReorder: 'Could not reorder.',
    loadingTheInstagramGrid: 'Loading the Instagram grid…',
    theInstagramGridPlannerIsNot: 'The Instagram grid planner is not available on this backend yet.',
    noInstagramPosts: 'No Instagram posts',
    schedulePostsToArrangeThemVisually: 'Schedule posts to arrange them visually in the feed grid.',
    dragTheScheduledPostsToArrange: 'Drag the scheduled posts to arrange your feed. Posts already live are fixed.',
    refresh: 'Refresh',
    reset: 'Reset',
    preview: 'Preview',
    apply: 'Apply',
    previewOfTheRecalculatedGridClick: 'Preview of the recalculated grid — click “Apply” to save the new order.',
    instagramFeedGrid: 'Instagram feed grid',
    moveEarlier: 'Move earlier',
    moveLater: 'Move later',
    reorderablePostSOutOfLength: 'reorderable post(s) out of {{length}}.',
  },
  linkShortener: {
    theLinkShortenerIsNotAvailable: '🔗 The link shortener is not available on this backend yet.',
    couldNotShorten: 'Could not shorten.',
    linksInTheMessage: '🔗 Links in the message',
    refreshClicks: 'Refresh clicks',
    click: 'click',
    shorten: 'Shorten',
    shortened: 'shortened',
    delete: 'Delete',
    deleteLink: 'Delete short link {{url}}',
    deleteTitle: 'Delete this short link?',
    deleteMessage: 'The link {{url}} and its click history will be permanently deleted. It will no longer redirect anywhere; the original URL will be put back in your message.',
    couldNotDelete: 'Could not delete.',
    myShortLinks: 'My short links ({{count}})',
  },
  networkCustomizer: {
    lengthChars: '{{length}} chars',
    inheritsTheSharedContent: '(inherits the shared content)',
    customizePerNetwork: 'Customize per network',
    onePostContentAdaptedToEach: 'One post, content adapted to each network: text, media, first comment and options specific to each platform. Without customization, the shared content above is published as-is.',
    network: 'network',
    custom: 'custom',
    networks: 'Networks',
    contentCustomizedForThisNetwork: 'Content customized for this network',
    customizeForLabel: 'Customize for {{label}}',
    destinations: 'Destinations:',
    postType: 'Post type',
    formatPublishedOnLabel: 'Format published on {{label}}.',
    sharedContentPublishedOnLabel: 'Shared content published on {{label}}',
    emptyText: '(empty text)',
    mediaItemS: 'media item(s)',
    shared1stComment: ' · shared 1st comment',
    exceedsTheLabelLimitCustomizeThe: ' · exceeds the {{label}} limit — customize the text for this network.',
    specificText: 'Specific text',
    textPublishedOnlyOnLabel: 'Text published only on {{label}}…',
    specificTitle: 'Specific title',
    titleForLabel: 'Title for {{label}}',
    specificMedia: 'Specific media',
    httpsImageJpg: 'https://…/image.jpg',
    removeTheMedia: 'Remove the media',
    addMedia: '+ Add media',
    uploadAiImportAUrl: 'Upload · AI · Import a URL',
    typesAcceptedOnLabel: 'Types accepted on {{label}}:',
    anEmptyListNoMediaOn: '. An empty list = no media on this network.',
    specificFirstComment: 'Specific first comment',
    firstCommentPublishedOnLabel: 'First comment published on {{label}}…',
    leaveEmptyToPublishNoFirst: 'Leave empty to publish no first comment on this network.',
    specificThreadSegments: 'Specific thread segments',
    messageV: 'Message {{v}}',
    removeTheMessage: 'Remove the message',
    addAMessage: '+ Add a message',
    specificLinkTrackingUtm: 'Specific link tracking (UTM)',
    sourceUtmSource: 'Source (utm_source)',
    mediumUtmMedium: 'Medium (utm_medium)',
    social: 'social',
    campaignUtmCampaign: 'Campaign (utm_campaign)',
    termUtmTerm: 'Term (utm_term)',
    contentUtmContent: 'Content (utm_content)',
    labelOptions: '{{label}} options',
    customizedV: 'Customized: {{v}}',
    sameAsTheSharedContent: 'Same as the shared content',
    mediacountMediaItemS: '🖼️ {{mediaCount}} media item(s)',
    noFirstCommentOnThisNetwork: '💬 No first comment on this network',
    instagramGridPlanner: 'Instagram grid planner',
  },
  networkOverrides: {
    n1Day: '1 day',
    n3Days: '3 days',
    n1Week: '1 week',
    n2Weeks: '2 weeks',
    feedPostDefault: 'Feed post (default)',
    reel: 'Reel',
    story24H: 'Story (24 h)',
    whoCanReply: 'Who can reply',
    everyoneDefault: 'Everyone (default)',
    mentionedAccounts: 'Mentioned accounts',
    followedAccounts: 'Followed accounts',
    visibility: 'Visibility',
    privateSelfOnlyTiktokDefault: 'Private — SELF_ONLY (TikTok default)',
    public: 'Public',
    friendsMutualFollows: 'Friends (mutual follows)',
    followers: 'Followers',
    withoutAnExplicitChoiceTiktokPublishes: 'Without an explicit choice, TikTok publishes the video privately (SELF_ONLY).',
    disableComments: 'Disable comments',
    disableDuet: 'Disable duet',
    disableStitch: 'Disable stitch',
    publicDefault: 'Public (default)',
    unlisted: 'Unlisted',
    private: 'Private',
    tagsCommaSeparated: 'Tags (comma-separated)',
    marketingSaas: 'marketing, saas',
    categoryId: 'Category (id)',
    n22PeopleBlogsDefault: '22 = People & Blogs (default).',
    madeForKids: 'Made for kids',
    postType: 'Post type',
    whatSNewStandardDefault: 'What\'s new — STANDARD (default)',
    event: 'Event',
    offer: 'Offer',
    alert: 'Alert',
    actionButton: 'Action button',
    none: 'None',
    learnMore: 'Learn more',
    book: 'Book',
    order: 'Order',
    buy: 'Buy',
    signUp: 'Sign up',
    call: 'Call',
    buttonUrl: 'Button URL',
    formatting: 'Formatting',
    plainTextDefault: 'Plain text (default)',
    linkLinkTypePost: 'Link (link-type post)',
    media: 'media',
    n1stComment: '1st comment',
  },
  networkPublishOptions: {
    enterThePollQuestion: 'Enter the poll question.',
    addAtLeastPollMinOptions: 'Add at least {{POLL_MIN_OPTIONS}} options.',
    optionsMustBeDistinct: 'Options must be distinct.',
    chooseADuration: 'Choose a duration.',
    labelPoll: '{{label}} poll',
    labelPollsAreNotEnabledOn: '{{label}} polls are not enabled on this backend yet. You can prepare the poll now: it will be published automatically once the publisher goes live (it will not be sent until then).',
    question: 'Question',
    whichFeatureDoYouPrefer: 'Which feature do you prefer?',
    optionsPollMinOptionsPollMax: 'Options ({{POLL_MIN_OPTIONS}}–{{POLL_MAX_OPTIONS}})',
    optionV: 'Option {{v}}',
    removeTheOption: 'Remove the option',
    addAnOption: '+ Add an option',
    duration: 'Duration',
    pollReadyOptioncountOptions: 'Poll ready · {{optionCount}} options',
    labelPostType: '{{label}} post type',
    format: 'Format',
    aReelOrAStoryRoutes: 'A Reel or a Story routes the post to the matching Facebook endpoint (media required).',
    the: 'The “',
    formatIsNotDeployedOnThis: '” format is not deployed on this backend yet; the post will go to the feed until the Reels/Stories publisher is in production.',
    reelsStoriesWillBeOfferedOnce: 'Reels / Stories will be offered once the matching publisher is deployed.',
    advancedPublishingOptions: 'Advanced publishing options',
    linkedinPollAndFacebookFormatFeed: 'LinkedIn poll and Facebook format (feed, Reel, Story). These settings apply to the relevant network only.',
  },
  postPreview: {
    yourProfile: 'Your profile',
    yourPostSTextWillAppear: '(Your post\'s text will appear here.)',
    like: '👍 Like',
    comment: '💬 Comment',
    repost: '↻ Repost',
    send: '➤ Send',
  },
  publishHistoryPage: {
    scheduled: 'Scheduled',
    retryCountAttemptS: '{{retry_count}} attempt(s)',
    mediacountMediaItemS: '🖼️ {{mediaCount}} media item(s)',
    n1stComment: '💬 1st comment',
    customizedV: 'Customized: {{v}}',
    scheduledOnV: 'Scheduled on {{v}}',
    publishedOnV: 'Published on {{v}}',
    createdOnV: 'Created on {{v}}',
    viewThePost: 'View the post',
    platformStatus: 'Platform status:',
    noPostIdentifierOnThePlatform: 'No post identifier on the platform — reaction not possible.',
    reactionsNotAvailableForThisPlatform: 'Reactions not available for this platform / account.',
    platformSideFailureTryAgainLater: 'Platform-side failure — try again later.',
    invalidReactionType: 'Invalid reaction type.',
    reactionBreakdown: 'Reaction breakdown',
    reactionBreakdownUnavailable: 'Reaction breakdown unavailable.',
    labelReactionSent: '“{{label}}” reaction sent.',
    reactToThePost: 'React to the post',
    impressions: 'Impressions',
    scope: 'Scope',
    engagement: 'Engagement',
    loading: 'Loading…',
    viewEngagement: 'View engagement',
    refresh: 'Refresh',
    fetchTheLikesCommentsSharesAnd: 'Fetch the likes, comments, shares and reach of this post.',
    fetchingStatistics: 'Fetching statistics…',
    likes: 'Likes',
    comm: 'Comm.',
    shares: 'Shares',
    measuredOnV: 'Measured on {{v}}',
    statisticsNotAvailableForThisPlatform: 'Statistics not available for this platform.',
    postApprovedItWillGoOut: 'Post approved — it will go out now or at the scheduled time.',
    rejectionReasonOptional: 'Rejection reason (optional):',
    postRejected: 'Post rejected.',
    loadingTheApprovalQueue: 'Loading the approval queue…',
    awaitingApproval: 'Awaiting approval',
    clickToPreviewAndEditBefore: 'Click to preview and edit before approving',
    previewEdit: 'Preview / edit',
    approve: 'Approve',
    reject: 'Reject',
    changesSavedAndPostApproved: 'Changes saved and post approved.',
    changesSaved: 'Changes saved.',
    previewAndApproveThePost: 'Preview and approve the post',
    processing: 'Processing…',
    saveAndApprove: 'Save and approve',
    preview: 'Preview',
    n1stComment2: '1st comment:',
    editBeforeApprovingTheChangesBelow: 'Edit before approving: the changes below are saved on this post ({{label}}) and this is the content that will be published once approved.',
    text: 'Text',
    theTextThisPostWillSend: 'The text this post will send to this network.',
    postText: 'Post text…',
    titleOptional: 'Title (optional)',
    title: 'Title',
    mediaOneUrlPerLine: 'Media (one URL per line)',
    replacesAllOfThisPostS: 'Replaces all of this post\'s media; empty = no media.',
    httpsImageJpg: 'https://…/image.jpg',
    firstCommentOptional: 'First comment (optional)',
    emptyNoFirstCommentOnThis: 'Empty = no first comment on this post.',
    postedAsACommentAfterThe: 'Posted as a comment after the post…',
    schedulingOptional: 'Scheduling (optional)',
    emptyPublishAsSoonAsApproved: 'Empty = publish as soon as approved.',
    tagPerformance: 'Tag performance',
    loadingTags: 'Loading tags…',
    noTrackedTags: 'No tracked tags',
    addHashtagsToYourPostsTo: 'Add hashtags to your posts to track their performance here.',
    tag: 'Tag',
    posts: 'Posts',
    platform: 'Platform',
    status: 'Status',
    details: 'Details',
    loadingStatistics: 'Loading statistics…',
    successRate: 'Success rate',
    published: 'Published',
    failures: 'Failures',
    volumeByPlatform: 'Volume by platform',
    recentPosts7DLoadedPage: 'Recent posts (7 d · loaded page)',
    postsPerDayOver7Days: 'Posts per day over 7 days (loaded page): {{v}}',
    noPostsToBreakDownYet: 'No posts to break down yet.',
    all: 'All',
    all2: 'All',
    accountPage: 'Account / page',
    allAccounts: 'All accounts',
    historyDisplay: 'History display',
    table: 'Table',
    cards: 'Cards',
    loadingTheHistory: 'Loading the history…',
    noPosts: 'No posts',
    yourPostsAndSchedulesWillAppear: 'Your posts and schedules will appear here.',
    pagePageTotalpagesHistorytotalPostS: 'Page {{page}} / {{totalPages}} · {{historyTotal}} post(s)',
    previous: 'Previous',
    next: 'Next',
    publishingAnalytics: 'Publishing analytics',
    analytics: 'Analytics',
    failedCountFailureS: '{{failed_count}} failure(s)',
    filterFailures: 'Filter failures',
    retryTheFailedJobsFromThe: 'Retry the failed jobs from the list (“Retry” button).',
    pendingCountPending: '{{pending_count}} pending',
    filterPendingJobs: 'Filter pending jobs',
    scheduledOrQueuedPosts: 'Scheduled or queued posts.',
    successpctSuccess: '{{successPct}}% success',
    publishedOutOfTotalJobsJob: 'published out of {{total_jobs}} job(s).',
    labelLeads: '{{label}} leads',
    filterThisPlatform: 'Filter this platform',
    jobSOnThisPlatform: 'job(s) on this platform.',
    jobsTodayToday: '{{jobs_today}} today',
    thisWeekJobsThisMonthThis: 'this week · {{jobs_this_month}} this month.',
    tryAgain: 'Try again',
    edit: 'Edit',
    cancel: 'Cancel',
    editTheScheduledPost: 'Edit the scheduled post',
    saving: 'Saving…',
    save: 'Save',
    currentlyV: 'Currently: {{v}}',
    newDateAndTime: 'New date and time',
    thePostWillGoOutAt: 'The post will go out at this new slot.',
    thisPostOnlyLabelTheOther: 'This post only ({{label}}) — the other networks of the same post are not changed.',
    pending: 'Pending',
    withdrawFromApproval: 'Withdraw from approval',
  },
  publishingLayout: {
    composer: 'Composer',
    history: 'History',
    publishing: 'Publishing',
    composeAndScheduleYourMultiPlatform: 'Compose and schedule your multi-platform posts and track their status.',
  },
  publishingPage: {
    imageType: 'image',
    textTooLongLengthMaxText: 'Text too long ({{length}}/{{max_text_length}}).',
    tooManyMediaMediacountMaxMedia: 'Too many media ({{mediaCount}}/{{max_media}}).',
    pollsAreNotSupported: 'Polls are not supported.',
    aVideoIsRequiredForThis: 'A video is required for this platform.',
    emptyContent: 'Empty content.',
    profile: 'Profile',
    companyPage: 'Company page',
    page: 'Page',
    businessAccount: 'Business account',
    expired: 'Expired',
    disconnected: 'Disconnected',
    pending: 'Pending',
    optionRequiredForLabelLabel2Customize: 'Option required for {{label}}: {{label2}} (“Customize per network” tab).',
    emptyThreadAddAtLeastOne: 'Empty thread — add at least one segment.',
    segmentVTooLongLengthMax: 'Segment {{v}} too long ({{length}}/{{max_text_length}}).',
    totalJobsPostSAwaitingApproval: '{{total_jobs}} post(s) awaiting approval — approve them in the history.',
    totalJobsPostSV: '{{total_jobs}} post(s) {{v}}.',
    draftSavedItWillBeRestored: 'Draft saved — it will be restored on reload.',
    savingTheDraftFailed: 'Saving the draft failed.',
    loadingCapabilities: 'Loading capabilities…',
    couldNotLoadThePublishingCapabilities: 'Could not load the publishing capabilities. {{error}}',
    noPlatformAvailable: 'No platform available',
    noPublishingPlatformIsActiveRight: 'No publishing platform is active right now.',
    analysisRecommendationsWhenAndWhatTo: 'Analysis & recommendations — when and what to publish',
    previousDraftRestored: 'Previous draft restored.',
    startOver: 'Start over',
    postImportedFromTheRichContent: 'Post imported from the Rich content studio.',
    viewHistory: 'View history',
    advancedOptions: 'Advanced options',
    destinations: 'Destinations',
    destination: 'destination',
    platform: 'platform',
    refreshTheDestinationListNewPages: 'Refresh the destination list (new pages detected)',
    refresh: '↻ Refresh',
    loadingAccounts: 'Loading accounts…',
    noConnectedAccount: 'No connected account',
    publishingTargetsSpecificAccountsProfile: 'Publishing targets specific accounts — profiles and pages — not just a platform. Connect at least one account to get started.',
    connectAnAccount: 'Connect an account',
    selectOneOrMoreProfilesAnd: 'Select one or more profiles and pages, including across several platforms. A separate post is created for each destination.',
    titleOptional: 'Title (optional)',
    internalTitleHook: 'Internal title / hook',
    text: 'Text',
    writeYourMessage: 'Write your message…',
    customized: ' (customized)',
    carouselMode: 'Carousel mode',
    carouselLengthSlideS: 'Carousel — {{length}} slide(s)',
    editTheSlides: 'Edit the slides',
    noSlidesAddAtLeastTwo: 'No slides — add at least two.',
    aCarouselNeedsAtLeastTwo: 'A carousel needs at least two media items.',
    theCarouselFormatAppliesToCompatible: 'The carousel format applies to compatible platforms; the others receive the images as attachments.',
    firstCommentOptional: 'First comment (optional)',
    postedAutomaticallyAsACommentAfter: 'Posted automatically as a comment after the post (links, hashtags…)',
    sentOnceThePostIsConfirmed: 'Sent once the post is confirmed, only on platforms that support comments.',
    linkTrackingUtm: '🔗 Link tracking (UTM)',
    composeAsAThread: 'Compose as a thread',
    supportSMultiMessageThreadsTurn: 'support(s) multi-message threads. Turn on thread mode to compose a series of messages posted as replies to each other; otherwise the text above is posted as a single message.',
    postType: 'Post type',
    preview: 'Preview',
    scheduling: 'Scheduling',
    now: 'Now',
    schedule: 'Schedule',
    dateAndTime: 'Date and time',
    bestTimesToPublish: '🕒 Best times to publish',
    requireApprovalBeforePublishing: 'Require approval before publishing',
    thePostWillBePlacedIn: 'The post will be placed in the approval queue and will only go out after a reviewer approves it (“Awaiting approval” tab in the history).',
    turnOnSoAReviewerApproves: 'Turn on so a reviewer approves the post before it goes out.',
    perPlatformCheck: 'Per-platform check',
    ready: 'Ready',
    issue: 'issue',
    noActiveAccount: 'No active account',
    custom: 'Custom',
    analyticsTips: 'Analytics & tips',
    readyPlatforms: 'Ready platforms',
    successHistory: 'Success (history)',
    freeText: 'free text',
    maxMediaMediaItemS: '{{max_media}} media item(s)',
    notSchedulable: 'not schedulable',
    n1stComment: '1st comment',
    accountsToReconnect: 'Accounts to reconnect',
    noActiveDestinationSelectedPublishingWil: '— no active destination selected. Publishing will fail until the account is reconnected.',
    manageAccounts: 'Manage accounts',
    scheduleForMoreImpact: 'Schedule for more impact',
    seeTheBestTimes: 'See the best times',
    publishingAtTheRightTimeImproves: 'Publishing at the right time improves reach — choose an optimal time per platform.',
    totalJobsPostSInTotal: '{{total_jobs}} post(s) in total',
    publishedPendingCountPending: 'published · {{pending_count}} pending ·',
    failed: 'failed.',
    history: 'History',
    theAiReviewRecommendsRevisitingThis: '⚠ The AI review recommends revisiting this content before publishing',
    scoreReviewscore100: ' (score {{reviewScore}}/100)',
    youCanStillPublishAfterConfirming: '— you can still publish after confirming.',
    selectAtLeastOneDestinationProfile: 'Select at least one destination (profile or page).',
    readOnlyPublishingIsReservedFor: 'Read-only — publishing is reserved for editors.',
    immediatePublishing: 'immediate publishing',
    yourPermissionsDoNotAllowSaving: 'Your permissions do not allow saving a draft.',
    saving: 'Saving…',
    saveDraft: 'Save draft',
    yourPermissionsDoNotAllowPublishing: 'Your permissions do not allow publishing.',
    sending: 'Sending…',
    sendForApproval: 'Send for approval',
    scheduleThePost: 'Schedule the post',
    publishNow: 'Publish now',
    aiReviewPublishAnyway: 'AI review: publish anyway?',
    cancelILlRevise: 'Cancel, I\'ll revise',
    publishAnyway: 'Publish anyway',
    youCanPublishAsIsOr: 'You can publish as-is, or cancel to adjust the text and rerun the review.',
    labelPublishingIsNotSupportedOn: '{{label}}: publishing is not supported on this deployment yet.',
    deselectAll: 'Deselect all',
    selectAll: 'Select all',
    comingSoonNotPublishable: 'Coming soon · not publishable',
    mediaUrl: 'Media (URL)',
    httpsImageJpg: 'https://…/image.jpg',
    removeTheMedia: 'Remove the media',
    addMedia: '+ Add media',
    uploadAiImportAUrl: 'Upload · AI · Import a URL',
    publicUrlsOnlyAcceptedTypes: 'Public URLs only. Accepted types:',
    carouselEditor: 'Carousel editor',
    done: 'Done',
    addReorderOrRemoveSlides2: 'Add, reorder or remove slides (2 to {{max}}). Each slide is a public media URL.',
    slideV: 'Slide {{v}}',
    moveTheSlideUp: 'Move the slide up',
    moveTheSlideDown: 'Move the slide down',
    removeTheSlide: 'Remove the slide',
    image: 'Image',
    video: 'Video',
    altTextAccessibility: 'Alt text (accessibility)',
    addASlide: '+ Add a slide',
    acceptedTypes: 'Accepted types:',
    enterAtLeastOneUtmParameter: 'Enter at least one UTM parameter.',
    previewUnavailable: 'Preview unavailable.',
    linkTrackingUtm2: 'Link tracking (UTM)',
    reset: 'Reset',
    apply: 'Apply',
    theParametersAreAddedToEvery: 'The parameters are added to every http(s) link in the message when publishing.',
    sourceUtmSource: 'Source (utm_source)',
    linkedin: 'linkedin',
    mediumUtmMedium: 'Medium (utm_medium)',
    social: 'social',
    campaignUtmCampaign: 'Campaign (utm_campaign)',
    summerLaunch: 'summer_launch',
    termUtmTerm: 'Term (utm_term)',
    optional: 'optional',
    contentUtmContent: 'Content (utm_content)',
    previewing: 'Previewing…',
    autoUtmPreview: 'Auto-UTM preview',
    noHttpSLinkDetectedIn: 'No http(s) link detected in the text — the UTMs will apply to the links you add.',
    suggestionsUnavailableRightNow: 'Suggestions unavailable right now.',
    bestTimesToPublish2: 'Best times to publish',
    platform2: 'Platform',
    heuristicSlotsTimeZoneBrowserTz: 'Heuristic slots (time zone {{BROWSER_TZ}}) based on typical engagement windows.',
    computingSlots: 'Computing slots…',
    noSlots: 'No slots',
    noSuggestionAvailable: 'No suggestion available.',
    threadSegmentsFilledMessageS: 'Thread segments — {{filled}} message(s)',
    messageV: 'Message {{v}}',
    moveTheMessageUp: 'Move the message up',
    moveTheMessageDown: 'Move the message down',
    removeTheMessage: 'Remove the message',
    threadMessageV: 'Thread message {{v}}…',
    addAMessage: '+ Add a message',
    eachMessageIsPostedAsA: 'Each message is posted as a reply to the previous one — the first one starts the thread.',
    pollOptional: 'Poll (optional)',
    question: 'Question',
    yourQuestion: 'Your question…',
    optionV: 'Option {{v}}',
    removeTheOption: 'Remove the option',
    addAnOption: '+ Add an option',
    enterAQuestionAndAtLeast: 'Enter a question and at least two options to attach a poll.',
    active: 'Active',
    scheduled: 'scheduled',
    launched: 'launched',
    schedule2: 'schedule',
  },
  queueCategoriesSection: {
    deleteTheCategoryName: 'Delete the category “{{name}}”?',
    categoryDeleted: 'Category deleted.',
    loading: 'Loading…',
    lengthCategoryIes: '{{length}} category(ies)',
    newCategory: '+ New category',
    noCategories: 'No categories',
    createACategoryAndGiveIt: 'Create a category and give it weekly slots to feed the evergreen queue.',
    active: 'Active',
    inactive: 'Inactive',
    noSlots: 'No slots',
    edit: 'Edit',
    deleting: 'Deleting…',
    delete: 'Delete',
    categoryCreated: 'Category created.',
    categoryUpdated: 'Category updated.',
    invalidTimeHhMmFormat24h: 'Invalid time — HH:MM format (24h).',
    thisSlotAlreadyExists: 'This slot already exists.',
    theNameIsRequired: 'The name is required.',
    newCategory2: 'New category',
    editTheCategory: 'Edit the category',
    cancel: 'Cancel',
    saving: 'Saving…',
    create: 'Create',
    save: 'Save',
    name: 'Name',
    eGTipsPromotions: 'e.g. Tips, Promotions…',
    color: 'Color',
    categoryColor: 'Category color',
    yes: 'Yes',
    no: 'No',
    weeklySlots: 'Weekly slots',
    eachSlotIsFilledInTurn: 'Each slot is filled in turn by a recyclable evergreen post from this category on “Fill the queue”.',
    removeTheSlot: 'Remove the slot',
    day: 'Day',
    time: 'Time',
    addTheSlot: '+ Add the slot',
  },
  queueContentQueuePage: {
    categories: 'Categories',
    evergreen: 'Evergreen',
    contentQueue: 'Content queue',
    recycleYourTimelessContentAndAutomate: 'Recycle your timeless content and automate publishing from RSS feeds — categories, evergreen and RSS, like SocialBee or Publer.',
    sections: 'Sections',
    sessionWithoutATenantSignIn: 'Session without a tenant — sign in again to manage the content queue.',
  },
  queueEvergreenSection: {
    categoryDeleted: 'Category deleted',
    uncategorized: 'Uncategorized',
    deleteThisEvergreenPost: 'Delete this evergreen post?',
    evergreenPostDeleted: 'Evergreen post deleted.',
    loading: 'Loading…',
    lengthEvergreenPostS: '{{length}} evergreen post(s)',
    firstCreateACategoryWithSlots: 'First create a category with slots',
    scheduleTheNextSlots: 'Schedule the next slots',
    fillTheQueue: 'Fill the queue',
    newEvergreenPost: '+ New evergreen post',
    noEvergreenPosts: 'No evergreen posts',
    createRecyclablePostsAndAttachThem: 'Create recyclable posts and attach them to a category; the queue will schedule them in turn.',
    recyclingOn: '♻ Recycling on',
    paused: 'Paused',
    publishedTimesPostedLastV: 'Published {{times_posted}}× · last: {{v}}',
    edit: 'Edit',
    deleting: 'Deleting…',
    delete: 'Delete',
    evergreenPostCreated: 'Evergreen post created.',
    evergreenPostUpdated: 'Evergreen post updated.',
    noSlotToScheduleNoUpcoming: 'No slot to schedule (no upcoming slot or no recyclable post).',
    scheduledSlotSScheduledTotalJobs: '{{scheduled}} slot(s) scheduled · {{total_jobs}} publishing job(s).',
    addSomeTextOrAtLeast: 'Add some text or at least one media item.',
    newEvergreenPost2: 'New evergreen post',
    editTheEvergreenPost: 'Edit the evergreen post',
    cancel: 'Cancel',
    saving: 'Saving…',
    create: 'Create',
    save: 'Save',
    platform: 'Platform',
    notEditableAfterCreation: 'Not editable after creation',
    category: 'Category',
    titleOptional: 'Title (optional)',
    internalTitleHook: 'Internal title / hook',
    content: 'Content',
    recyclablePostText: 'Recyclable post text…',
    accountOptional: 'Account (optional)',
    emptyAllActiveAccountsOnThe: 'Empty → all active accounts on the platform',
    socialAccountId: 'Social account ID',
    mediaOneUrlPerLine: 'Media (one URL per line)',
    https: 'https://…',
    recyclingOnEligibleForAutomaticQueue: 'Recycling on (eligible for automatic queue filling)',
    scheduling: 'Scheduling…',
    schedule: 'Schedule',
    schedulesTheNextSlotsOfEach: 'Schedules the next slots of each active category by picking, in turn, the least recently published recyclable evergreen post. The posts become normal scheduled jobs.',
    allActiveCategories: 'All active categories',
    horizonDays: 'Horizon (days)',
    maxSlotsOptional: 'Max slots (optional)',
    unlimited: 'unlimited',
    n1To90: '1 to 90',
    n1To500: '1 to 500',
  },
  queueRssSection: {
    feedCount: '{{count}} feed(s)',
    categoryDeleted: 'Category deleted',
    uncategorized: 'Uncategorized',
    deleteThisRssFeed: 'Delete this RSS feed?',
    feedDeleted: 'Feed deleted.',
    feedUnreachableNoItemCreated: 'Feed unreachable — no item created.',
    noNewItems: 'No new items.',
    loading: 'Loading…',
    addAFeed: '+ Add a feed',
    noRssFeeds: 'No RSS feeds',
    addAnRssAtomFeedIts: 'Add an RSS/Atom feed; its new articles will become drafts (or scheduled posts if auto-publishing is on).',
    autoPublishing: 'Auto-publishing',
    draft: 'Draft',
    active: 'Active',
    inactive: 'Inactive',
    lastFetchVSeenCountItem: 'Last fetch: {{v}} · {{seen_count}} item(s) seen',
    disable: 'Disable',
    enable: 'Enable',
    fetching: 'Fetching…',
    fetchNow: 'Fetch now',
    deleting: 'Deleting…',
    delete: 'Delete',
    feedAdded: 'Feed added.',
    invalidUrlItMustStartWith: 'Invalid URL — it must start with http(s)://.',
    addAnRssFeed: 'Add an RSS feed',
    cancel: 'Cancel',
    adding: 'Adding…',
    add: 'Add',
    feedUrlRssAtom: 'Feed URL (RSS/Atom)',
    httpsExampleComFeedXml: 'https://example.com/feed.xml',
    targetPlatform: 'Target platform',
    category: 'Category',
    accountOptional: 'Account (optional)',
    emptyAllActiveAccountsOnThe: 'Empty → all active accounts on the platform',
    socialAccountId: 'Social account ID',
    templateOptional: 'Template (optional)',
    placeholdersTitleLinkSummaryPublishedEmp: 'Placeholders: {title} {link} {summary} {published}. Empty → title + link.',
    titleLink: '{title}\n\n{link}',
    autoPublishingSchedulesNewArticlesInstea: 'Auto-publishing (schedules new articles instead of only drafting them)',
    activeFetchedAutomaticallyByTheScheduler: 'Active (fetched automatically by the scheduler)',
    fetchResult: 'Fetch result',
    close: 'Close',
    reachable: 'Reachable',
    unreachable: 'Unreachable',
    newItemS: 'new item(s)',
    jobSScheduled: 'job(s) scheduled',
    itemsLength: 'Items ({{length}})',
  },
  reviewPanel: {
    expert: 'Expert',
    evaluator: 'Evaluator',
    critic: 'Critic',
    judge: 'Judge',
    couldNotGenerateThePanelTry: 'Could not generate the panel — try again.',
    reviewFailedTryAgain: 'Review failed — try again.',
    aiReviewMultiAgentPanel: '🧠 AI review (multi-agent panel)',
    ready: '✓ Ready',
    needsWork: '↻ Needs work',
    contentTheme: 'Content theme',
    eGAgenticArchitectureAiSafety: 'e.g. Agentic architecture, AI safety…',
    experts: 'Experts',
    fewerExperts: 'Fewer experts',
    moreExperts: 'More experts',
    assemblingThePanel: 'Assembling the panel…',
    regenerateThePanel: '↻ Regenerate the panel',
    generateThePanelFromTheTheme: '✨ Generate the panel from the theme',
    panel: 'Panel',
    derivedByTheAiFromThe: 'derived by the AI from the theme',
    generatedThemeInjected: 'generated (theme injected)',
    checkUncheckEditTheRolesOr: '— check/uncheck, edit the roles, or add experts.',
    enableV: 'Enable {{v}}',
    roleToDefine: '(role to define)',
    close: 'Close',
    edit: '✎ Edit',
    removeThisExpert: 'Remove this expert',
    role: 'Role',
    expertise: 'Expertise',
    skillsCommaSeparated: 'Skills (comma-separated)',
    evaluationAngle: 'Evaluation angle',
    addAnExpert: '+ Add an expert',
    enabledcountActiveAgentS: '{{enabledCount}} active agent(s)',
    reviewInProgress: 'Review in progress…',
    startTheContentReview: '🔍 Start the content review',
    writeTheContentToAssessTo: 'Write the content to assess to start the review.',
    panelVerdict: 'Panel verdict',
    recommendationPublish: '✓ Recommendation: publish',
    recommendationReviseBeforePublishing: '↻ Recommendation: revise before publishing',
    modelModel: 'Model: {{model}}',
  },
  studioBoost: {
    chooseAnAdAccountToBoost: 'Choose an ad account to boost (Advertising → connect an account).',
    enterABoostBudgetGreaterThan: 'Enter a boost budget greater than 0.',
  },
  studioBulkMediaStudioPage: {
    text: 'Text',
    postFeed: 'Post (feed)',
    video: 'Video',
    someSelectedMediaCannotBeFound: 'Some selected media cannot be found in the media library.',
    couldNotLoadTheSelectedMedia: 'Could not load the selected media.',
    uploadFailed: 'Upload failed.',
    thisUrlWasReadButCould: 'This URL was read but could not be saved as schedulable media (paste a YouTube/Vimeo video URL).',
    videoAddedFromTheUrlPublished: 'Video added from the URL (published as a link/video).',
    mediaAddedFromTheUrl: 'Media added from the URL.',
    couldNotImportThisMediaPage: 'Could not import this media (page unreachable or protected).',
    addACaptionPerMediaOr: 'Add a caption (per media or global) before generating per network.',
    selectAtLeastOnePlatform: 'Select at least one platform.',
    immediatePublishing: 'Immediate publishing',
    fromBaseToLastEverySpacingminutes: 'From {{base}} to {{last}} (every {{spacingMinutes}} min)',
    jobsPostSScheduled: '{{jobs}} post(s) scheduled.',
    bulkMediaStudio: 'Bulk media studio',
    uploadOrAddByUrlSeveral: 'Upload (or add by URL) several media items, then schedule them all at once — post, story, reel or carousel — on every network.',
    postHistory: 'Post history',
    publishingCapabilitiesUnavailableCapserr: 'Publishing capabilities unavailable. {{capsError}}',
    mediaReady: 'Media ready',
    inProgress: 'In progress',
    platforms: 'Platforms',
    scheduling: 'Scheduling',
    files: 'Files',
    fromAUrl: 'From a URL',
    dragAndDropSeveralFilesHere: 'Drag and drop several files here',
    imagesVideosGifsAudioOrPdf: 'Images, videos, GIFs, audio or PDF — stored on OVH',
    browse: 'Browse…',
    urlOfAVideoYoutubeOr: 'URL of a video (YouTube…) or an image',
    httpsWwwYoutubeComWatchV: 'https://www.youtube.com/watch?v=…',
    add: 'Add',
    theMediaIsStoredOnThe: 'The media is stored on the server, then added to the list to schedule.',
    noMediaYet: 'No media yet',
    uploadFilesOrAddAUrl: 'Upload files or add a URL — they will appear here, ready to schedule.',
    linkVideo: '🔗 link / video',
    ready: 'Ready',
    failed: 'Failed',
    removeFilename: 'Remove {{filename}}',
    queued: 'Queued',
    postTypeThisMedia: 'Post type (this media)',
    globalV: 'Global ({{v}})',
    boostAsAnAd: '🚀 Boost as an ad',
    captionOptionalOtherwiseTheGlobalCaption: 'Caption (optional — otherwise the global caption)',
    captionForThisMedia: 'Caption for this media…',
    generateAdaptPerNetwork: '✨ Generate / adapt per network',
    chars: 'chars)',
    targetNetworks: 'Target networks',
    loadingNetworks: 'Loading networks…',
    noActivePublishingPlatform: 'No active publishing platform.',
    globalSettings: 'Global settings',
    defaultPostType: 'Default post type',
    globalCaptionAppliedToMediaWithout: 'Global caption (applied to media without their own caption)',
    sharedCaption: 'Shared caption…',
    groupIntoASingleCarousel: 'Group into a single carousel',
    boostAllMediaAsAds: '🚀 Boost all media as ads',
    noLinkedAdAccount: 'No linked ad account.',
    connectAnAccount: 'Connect an account',
    toBoost: 'to boost.',
    adAccount: 'Ad account',
    dailyBudgetPerMedia: 'Daily budget per media',
    objective: 'Objective',
    durationDaysOptional: 'Duration (days, optional)',
    schedulingCadence: 'Scheduling cadence',
    firstSlotEmptyImmediate: 'First slot (empty = immediate)',
    spacingBetweenMediaMinutes: 'Spacing between media (minutes)',
    scheduling2: 'Scheduling…',
    scheduleAllLengthMedia: 'Schedule all ({{length}} media)',
    waitForTheUploadsToFinish: 'Wait for the uploads to finish before scheduling.',
    result: 'Result',
    viewHistory: 'View history →',
    postSCreatedForLengthMedia: 'post(s) created for {{length}} media item(s).',
    mediaVError: 'Media {{v}}: {{error}}',
    mediaVV2JobS: 'Media {{v}}: {{v2}} job(s)',
    story: 'Story',
    reel: 'Reel',
    carousel: 'Carousel',
    scheduled: 'Scheduled',
    immediate: 'Immediate',
  },
  tikTokOptionsPanel: {
    disableComments: 'Disable comments',
    disableDuet: 'Disable duet',
    disableStitch: 'Disable stitch',
    couldNotReadTheTiktokAccount: 'Could not read the TikTok account settings.',
    accountSettings: 'Account settings',
    appliedBelow: 'applied below.',
    theTiktokAccountSettingsAreNot: 'The TikTok account settings are not available — every option is offered; TikTok will refuse an unauthorized level at publishing time.',
    visibility: 'Visibility',
    loadingAllowedLevels: 'Loading allowed levels…',
    thisAccountCanOnlyPublishPrivately: 'This account can only publish privately (app not audited by TikTok).',
    levelsLimitedToWhatThisTiktok: 'Levels limited to what this TikTok account allows.',
    lockedThisAccountHasDisabledThis: '— locked: this account has disabled this interaction on TikTok.',
  },
  ui: {
    tryAgain: 'Try again',
    close: 'Close',
    loading: 'Loading…',
  },
  widgets: {
    trendOverLengthPointsLatestLast: 'Trend over {{length}} points (latest: {{last}})',
  },
};

export default en;
